// Admin CRUD for `locations` — the list of places stock can be.
//
// WHY THIS PAGE EXISTS AT ALL
// Firefly's five locations (Headquarters, At Event, At Tour, At Customer,
// Unknown) were inserted by a pre-baseline seed script that never became part of
// the migration chain. Every database built from 001 onward therefore gets the
// TABLE and none of the ROWS — which is why SwarmSystems could not receive
// anything: the receive drawer's location dropdown was empty, and there was no
// screen anywhere in the app that could add one.
//
// The fix is deliberately a screen and not a seed migration. Places are
// per-company data; neither company works out of the other's building, and
// putting Firefly's addresses into Swarm's database would be inventing facts.
//
// ── VENDOR-BACKED ROWS ──────────────────────────────────────────────────────
// A row with a vendor_id is a contract assembler's premises, created by
// switching on "can receive" for that vendor. Its name and type belong to the
// vendor record, so they are read-only here and the row links back. Everything
// else (address, retire) works the same as any other location.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import DrawerShell from '../Receiving/DrawerShell';
import HelpDrawer from '../components/HelpDrawer';
import { DataTable } from '../components/table/DataTable';
import { usePullsApi, type PullSettings } from '../Parts/Pulls/pullsApi';
import SectionChip from '../components/SectionChip';
import { openAuthedPdf } from '../Receiving/openAuthedPdf';
import AreaGridForm from './AreaGridForm';
import { gridExtent } from './sectionGrid';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { Field } from '../components/ui/Field';
import Select from '../components/ui/Select';
import Switch from '../components/ui/Switch';
import Tooltip from '../components/ui/Tooltip';
import { Alert } from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';

export interface LocationAdminRow {
  id: number;
  name: string;
  type: 'warehouse' | 'event' | 'tour' | 'customer' | 'unknown' | 'vendor' | 'qc_hold' | 'scrap' | 'repair' | 'flashed' | 'legacy' | 'area' | 'section';
  /** Set on areas (PLLT) and sections (PLLT-B3). */
  code?: string | null;
  parent_location_id: number | null;
  address: string | null;
  is_active: boolean;
  /** Shelf stock: received into, picked from. */
  is_inventory: boolean;
  /** The run pool. At most one active. */
  is_assembly: boolean;
  vendor_id: string | null;
  vendor_name: string | null;
  vendor_code: string | null;
}

// 'vendor' is absent on purpose: those rows are born from the vendor toggle, and
// the API refuses to create one here (vendor_type_not_assignable).
const CREATABLE_TYPES: { value: LocationAdminRow['type']; label: string; hint: string }[] = [
  { value: 'warehouse', label: 'Warehouse', hint: 'A building you run. Switch on Inventory in the list to receive and pick there.' },
  { value: 'event', label: 'Event', hint: 'Gear that is out at a show.' },
  { value: 'tour', label: 'Tour', hint: 'Gear traveling with a tour.' },
  { value: 'customer', label: 'Customer', hint: 'Gear sitting with a customer.' },
  { value: 'unknown', label: 'Unknown', hint: 'Where something is when nobody is sure.' },
  // 037. ONE OF EACH PER DEPLOYMENT. Receiving looks them up by type rather
  // than asking anybody to pick, so a second active one of either makes the
  // lookup ambiguous and rejections start failing with a message telling you to
  // retire the spare. The name is where the building goes.
  {
    value: 'qc_hold',
    label: 'QC Hold',
    hint: 'Arrived, not accepted, decision pending. Stock here does not count toward what you can build.',
  },
  {
    value: 'scrap',
    label: 'Scrap',
    hint: 'Written off. The units stay in the history but count nowhere. Without this, QC Hold has no exit.',
  },
  {
    value: 'flashed',
    label: 'Flashed boards',
    hint: 'Where boards wait after flashing, before they are fitted. One per deployment.',
  },
];

const TYPE_LABELS: Record<LocationAdminRow['type'], string> = {
  warehouse: 'Warehouse',
  event: 'Event',
  tour: 'Tour',
  customer: 'Customer',
  unknown: 'Unknown',
  vendor: 'Vendor',
  qc_hold: 'QC Hold',
  scrap: 'Scrap',
  repair: 'Repair',
  flashed: 'Flashed boards',
  legacy: 'Legacy boards',
  area: 'Area',
  section: 'Section',
};

type StockRole = 'is_inventory' | 'is_assembly';

// The server refuses a stock role on these; they hold stock for other reasons.
const ROLELESS_TYPES: LocationAdminRow['type'][] = ['vendor', 'qc_hold', 'scrap', 'repair', 'flashed', 'legacy', 'area', 'section'];

const ROLES: { field: StockRole; label: string; hint: string }[] = [
  { field: 'is_inventory', label: 'Inventory', hint: 'Shelf stock. Boxes are received here and picked from here.' },
  { field: 'is_assembly', label: 'Assembly', hint: 'The pool the runs build from. Picks land here. Only one place can be Assembly.' },
];

function roleSaved(name: string, field: StockRole, on: boolean): string {
  if (field === 'is_assembly') return on ? `${name} is now the Assembly.` : `${name} is no longer the Assembly.`;
  return on ? `${name} now holds Inventory.` : `${name} no longer holds Inventory.`;
}

function roleRefused(err: any): string {
  const message = err?.data?.error?.message;
  if (message) return message;
  if (err?.data?.error?.code === 'assembly_taken') {
    return 'Another location is already the Assembly. Switch Assembly off there first, then here.';
  }
  return 'The stock role did not change. Try again.';
}

/** The house switch: the kit's Switch, named by row for a screen reader, with its hint as the tooltip. */
const RoleSwitch: React.FC<{
  label: string;
  hint: string;
  checked: boolean;
  rowName: string;
  onChange: (on: boolean) => void;
}> = ({ label, hint, checked, rowName, onChange }) => (
  <Tooltip label={hint}>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={`${label} at ${rowName}`}
      label={label}
    />
  </Tooltip>
);

const REPAIR_ONLY_HELP =
  'When on, a repair pull must come from Repair or the scrap bin. Turn it off to let repairs pull from any shelf.';

const BOARDS_BY_QR_HELP =
  'When on, autopilots, ESCs and other flashed boards are pulled by their QR code, never by counting from a box, so a board is never charged twice. Turn it on once boards can get a sticker without being flashed. Scrap by count stays open.';

// The two per-database pull switches, each drawn on the place it governs.
const PULL_SWITCHES: Record<string, { key: keyof PullSettings; label: string; hint: string }> = {
  repair: { key: 'repairFromRepairOnly', label: 'Repair parts only from here', hint: REPAIR_ONLY_HELP },
  flashed: { key: 'boardsByQrOnly', label: 'Boards pull by QR only', hint: BOARDS_BY_QR_HELP },
};

const labelClass = 'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1';
const btnPrimary = buttonClass('filled');
const btnGhost = buttonClass('quiet');

/**
 * The wait, at the rhythm of the rows it stands in for.
 *
 * Per-column bars cannot survive the move to the shared table: it renders ONE
 * full-width loading row, so there are no columns to lay them across. Stacked
 * bars at the row pitch still do the skeleton's actual job, which is keeping the
 * page from jumping when the data lands.
 */
const LoadingBars: React.FC = () => (
  <SkeletonGroup label="Loading locations" className="px-4 py-8 space-y-3">
    {['w-48', 'w-32', 'w-40'].map((width) => (
      <Skeleton key={width} className={`h-3 ${width}`} />
    ))}
  </SkeletonGroup>
);

// ---------------------------------------------------------------------------
// The location table: the shared DataTable's columns.
//
// Module scope, per the DataTable contract — the array never changes identity
// and no cell closes over page state. Edit and Retire arrive on the table's
// `meta` and are read at render time; never wrap one of these cells in
// React.memo, which would skip the parent's render and read stale meta.
//
// The retired row's dimming is per-row, so it is `rowProps` on the page rather
// than a class on any column: a static column class would dim every row.
//
// No `sortable` and no accessorFn: the list is short, the API's order is the
// one the page has always shown, and the retired-row filter above the table is
// the only thing that changes what is in it.
// ---------------------------------------------------------------------------

interface LocationTableMeta {
  onEdit: (row: LocationAdminRow) => void;
  setActive: (row: LocationAdminRow, is_active: boolean) => void;
  setRole: (row: LocationAdminRow, field: StockRole, on: boolean) => void;
  /** null until the settings have loaded; the switches are not drawn before then. */
  pullSettings: PullSettings | null;
  setPullSetting: (key: keyof PullSettings, on: boolean) => void;
  onAddArea: (parent: LocationAdminRow) => void;
  onGrow: (area: LocationAdminRow) => void;
  onPrint: (row: LocationAdminRow, all: boolean) => void;
  expanded: ReadonlySet<number>;
  onToggle: (area: LocationAdminRow) => void;
  gridOf: (area: LocationAdminRow) => { columns: number; rows: number; active: number };
  /** Inline refusals from retiring an area or section, by row id. */
  errors: Record<number, string>;
  /** False for a retired area and for any section under one: the server refuses reviving them. */
  canRestore: (row: LocationAdminRow) => boolean;
}

const RETIRED_AREA_NOTE =
  'A retired area stays retired. Add it again with the same code: its printed labels still scan.';

const LOCATION_COLUMNS: ColumnDef<LocationAdminRow, any>[] = [
  {
    id: 'name',
    header: 'Name',
    cell: (info) => {
      const r = info.row.original;
      const m = info.table.options.meta as LocationTableMeta;
      const err = m.errors[r.id];
      const errorLine = (
        <Reveal show={!!err}>
          <div className="pt-0.5">
            <Alert tone="error" variant="text" className="whitespace-normal !text-xs">
              {err}
            </Alert>
          </div>
        </Reveal>
      );
      if (r.type === 'section') {
        return (
          <div className="pl-12">
            <span className="inline-flex items-center gap-2">
              <SectionChip code={r.code || r.name} />
              <Appear show={!r.is_active} className="text-xs text-on-surface-variant">retired</Appear>
            </span>
            {errorLine}
          </div>
        );
      }
      if (r.type === 'area') {
        const open = m.expanded.has(r.id);
        const g = m.gridOf(r);
        return (
          <div className="pl-5">
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-expanded={open}
                aria-label={`${open ? 'Hide' : 'Show'} the sections of ${r.name}`}
                onClick={() => m.onToggle(r)}
                className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-container-high focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16"
                  fill="none"
                  className={`h-3 w-3 transition-transform duration-150 ease-emphasized motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}
                >
                  <path fill="currentColor" d="M6 3.5 10.5 8 6 12.5z" />
                </svg>
              </button>
              <span className="text-on-surface">{r.name}</span>
              {r.code && <span className="font-mono text-xs tabular-nums text-on-surface-variant">{r.code}</span>}
              <Appear show={!r.is_active} className="text-xs text-on-surface-variant">retired</Appear>
            </div>
            {g.columns > 0 && (
              <p className="pl-7 text-xs tabular-nums text-on-surface-variant">
                {g.columns} by {g.rows}, {g.active} {g.active === 1 ? 'section' : 'sections'}
              </p>
            )}
            <Reveal show={!r.is_active && !err}>
              <p className="pl-7 text-xs text-on-surface-variant">{RETIRED_AREA_NOTE}</p>
            </Reveal>
            {errorLine}
          </div>
        );
      }
      return (
        <>
          <span className="text-on-surface">{r.name}</span>
          {!r.is_active && (
            <span className="ml-2 text-[10px] uppercase tracking-wider text-on-surface-variant">
              retired
            </span>
          )}
          {r.vendor_id && (
            <Link
              to="/admin/vendors"
              className="block text-xs text-on-surface-variant hover:text-primary transition-colors"
            >
              from vendor {r.vendor_code ? `${r.vendor_name} (${r.vendor_code})` : r.vendor_name}
            </Link>
          )}
          {errorLine}
        </>
      );
    },
  },
  {
    id: 'type',
    header: 'Type',
    cell: ({ row }) => {
      const r = row.original;
      return (
        <span
          className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${
            r.type === 'warehouse'
              ? 'border-primary/40 text-primary'
              : r.type === 'vendor'
                ? 'border-outline text-on-surface'
                // Amber for the two places stock goes when
                // something is wrong with it. Not an error state,
                // but not somewhere you want to see a growing
                // number either, and the color says which rows
                // to look at twice.
                : r.type === 'qc_hold' || r.type === 'scrap'
                  ? 'border-amber-500/40 text-amber-400/90'
                  : 'border-gray-700 text-on-surface-variant'
          }`}
        >
          {TYPE_LABELS[r.type]}
        </span>
      );
    },
  },
  {
    id: 'roles',
    header: 'Stock role',
    meta: { cellClass: 'whitespace-nowrap' },
    cell: (info) => {
      const { setRole, pullSettings, setPullSetting } = info.table.options.meta as LocationTableMeta;
      const r = info.row.original;
      const sw = PULL_SWITCHES[r.type];
      if (sw && pullSettings) {
        return (
          <div className="max-w-sm">
            <RoleSwitch
              label={sw.label}
              hint={sw.hint}
              checked={pullSettings[sw.key]}
              rowName={r.name}
              onChange={(on) => setPullSetting(sw.key, on)}
            />
            <p className="mt-0.5 text-xs text-on-surface-variant whitespace-normal">{sw.hint}</p>
          </div>
        );
      }
      if (ROLELESS_TYPES.includes(r.type)) {
        return (
          <span className="text-xs text-on-surface-variant" title="This kind of place holds stock for its own reason, never for picking or building.">
            —
          </span>
        );
      }
      return (
        <div className="flex items-center gap-4">
          {ROLES.map((role) => (
            <RoleSwitch
              key={role.field}
              label={role.label}
              hint={role.hint}
              checked={!!r[role.field]}
              rowName={r.name}
              onChange={(on) => setRole(r, role.field, on)}
            />
          ))}
        </div>
      );
    },
  },
  {
    id: 'address',
    header: 'Address',
    // The td is the element that truncates, so the tooltip stays on it.
    meta: {
      cellClass: 'text-on-surface-variant max-w-md truncate',
      cellAttrs: (r: LocationAdminRow) => ({ title: r.address || '' }),
    },
    cell: ({ row }) => row.original.address || '—',
  },
  {
    id: 'actions',
    header: 'Actions',
    meta: { align: 'right', cellClass: 'whitespace-nowrap' },
    cell: (info) => {
      const { onEdit, setActive, onAddArea, onGrow, onPrint, canRestore } = info.table.options.meta as LocationTableMeta;
      const r = info.row.original;
      const ACTION = buttonClass('quiet', 'sm');
      const isGridRow = r.type === 'area' || r.type === 'section';
      return (
        <>
          {r.is_active && r.is_inventory && !isGridRow && (
            <Tooltip label="Add a rack with a grid of labelled sections">
              <button type="button" onClick={() => onAddArea(r)} aria-label={`Add area to ${r.name}`} className={ACTION}>
                Add area
              </button>
            </Tooltip>
          )}
          {r.type === 'area' && r.is_active && (
            <>
              <Tooltip label="Make the grid bigger. Sections are only ever added">
                <button type="button" onClick={() => onGrow(r)} aria-label={`Grow grid of ${r.name}`} className={ACTION}>
                  Grow grid
                </button>
              </Tooltip>
              <Tooltip label="Open one label per section, ready to print">
                <button type="button" onClick={() => onPrint(r, true)} aria-label={`Print labels for ${r.name}`} className={ACTION}>
                  Print labels
                </button>
              </Tooltip>
            </>
          )}
          {r.type === 'section' && r.is_active && (
            <Tooltip label="Open this section's label, ready to print">
              <button type="button" onClick={() => onPrint(r, false)} aria-label={`Print label for ${r.code || r.name}`} className={ACTION}>
                Print label
              </button>
            </Tooltip>
          )}
          {!isGridRow && (
            <button
              type="button"
              onClick={() => onEdit(r)}
              className={ACTION}
            >
              Edit
            </button>
          )}
          {(r.is_active || canRestore(r)) && (
            <button
              type="button"
              onClick={() => setActive(r, !r.is_active)}
              aria-label={`${r.is_active ? 'Retire' : 'Restore'} ${r.type === 'section' ? r.code || r.name : r.name}`}
              className={ACTION}
            >
              {r.is_active ? 'Retire' : 'Restore'}
            </button>
          )}
        </>
      );
    },
  },
];

const LocationsAdmin: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  const isAdmin = user.isadmin || user.can_admin;
  const pulls = usePullsApi();

  const [rows, setRows] = useState<LocationAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRetired, setShowRetired] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<LocationAdminRow | null>(null);
  const [pullSettings, setPullSettings] = useState<PullSettings | null>(null);
  const [addingAreaTo, setAddingAreaTo] = useState<LocationAdminRow | null>(null);
  const [growing, setGrowing] = useState<LocationAdminRow | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set());
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});

  const fetchRows = async () => {
    setLoading(true);
    try {
      // Always fetch everything; the toggle filters client-side so flipping it
      // is instant rather than a round trip.
      const data = (await api.get('/locations?include_inactive=1')) as LocationAdminRow[];
      setRows(data || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSwitchRow = rows.some((r) => !!PULL_SWITCHES[r.type]);
  useEffect(() => {
    if (!hasSwitchRow || !isAdmin) return;
    pulls
      .settings()
      .then((s) => setPullSettings({ repairFromRepairOnly: !!s?.repairFromRepairOnly, boardsByQrOnly: !!s?.boardsByQrOnly }))
      .catch(() => {
        // Without the settings there is nothing to switch; the rows keep their dash.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSwitchRow, isAdmin]);

  const SAVED: Record<keyof PullSettings, [string, string]> = {
    repairFromRepairOnly: ['Repairs now pull only from Repair or the scrap bin.', 'Repairs can now pull from any shelf.'],
    boardsByQrOnly: ['Boards now pull by their QR code only.', 'Boards can now be pulled by count again.'],
  };

  const setPullSetting = async (key: keyof PullSettings, on: boolean) => {
    const before = pullSettings;
    setPullSettings((s) => (s ? { ...s, [key]: on } : s));
    try {
      const saved = await pulls.saveSettings({ [key]: on });
      setPullSettings({ repairFromRepairOnly: !!saved?.repairFromRepairOnly, boardsByQrOnly: !!saved?.boardsByQrOnly });
      toast.success(SAVED[key][on ? 0 : 1]);
    } catch (err: any) {
      setPullSettings(before);
      toast.error(err?.data?.error?.message || err?.message || 'The setting did not save. Try again.');
    }
  };

  // Areas sit under their warehouse and sections under their area, collapsed until the area is opened.
  const visible = useMemo(() => {
    const shown = showRetired ? rows : rows.filter((r) => r.is_active);
    const kids = new Map<number, LocationAdminRow[]>();
    const top: LocationAdminRow[] = [];
    for (const r of shown) {
      if ((r.type === 'area' || r.type === 'section') && r.parent_location_id != null) {
        const list = kids.get(r.parent_location_id) ?? [];
        list.push(r);
        kids.set(r.parent_location_id, list);
      } else top.push(r);
    }
    const out: LocationAdminRow[] = [];
    const walk = (r: LocationAdminRow) => {
      out.push(r);
      if (r.type === 'section') return;
      if (r.type === 'area' && !expanded.has(r.id)) return;
      (kids.get(r.id) ?? []).forEach(walk);
    };
    top.forEach(walk);
    return out;
  }, [rows, showRetired, expanded]);

  const gridOf = useMemo(() => {
    const byArea = new Map<number, LocationAdminRow[]>();
    for (const r of rows) {
      if (r.type === 'section' && r.parent_location_id != null) {
        byArea.set(r.parent_location_id, [...(byArea.get(r.parent_location_id) ?? []), r]);
      }
    }
    return (area: LocationAdminRow) => {
      const secs = byArea.get(area.id) ?? [];
      const { columns, rows: n } = gridExtent(area.code || '', secs.map((s) => s.code || ''));
      return { columns, rows: n, active: secs.filter((s) => s.is_active).length, total: secs.length };
    };
  }, [rows]);
  const retiredCount = useMemo(() => rows.filter((r) => !r.is_active).length, [rows]);
  const receivableCount = useMemo(
    () => rows.filter((r) => r.is_active && (r.is_inventory || r.type === 'vendor')).length,
    [rows]
  );

  const printLabels = async (row: LocationAdminRow, all: boolean) => {
    try {
      await openAuthedPdf(all ? `/locations/${row.id}/section-labels.pdf` : `/locations/${row.id}/label.pdf`);
    } catch {
      toast.error('The labels did not open. Check the pop-up blocker and try again.');
    }
  };

  const setActive = async (row: LocationAdminRow, is_active: boolean) => {
    setRowErrors((prev) => {
      if (!(row.id in prev)) return prev;
      const { [row.id]: _gone, ...rest } = prev;
      return rest;
    });
    try {
      const updated = (await api.patch(`/locations/${row.id}`, { body: { is_active } })) as LocationAdminRow;
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
      // An area retires its sections with it; the list is the only place that says so.
      if (!is_active && row.type === 'area') {
        setRows((prev) => prev.map((r) => (r.parent_location_id === row.id ? { ...r, is_active: false } : r)));
      }
      toast.success(is_active ? `${row.name} is back in service` : `${row.name} retired`);
    } catch (err: any) {
      const message = err?.data?.error?.message || err?.message || 'Update failed';
      if (row.type === 'area' || row.type === 'section') setRowErrors((prev) => ({ ...prev, [row.id]: message }));
      else toast.error(message);
    }
  };

  const setRole = async (row: LocationAdminRow, field: StockRole, on: boolean) => {
    const put = (value: boolean) =>
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, [field]: value } : r)));
    put(on);
    try {
      const updated = (await api.patch(`/locations/${row.id}`, { body: { [field]: on } })) as LocationAdminRow;
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
      toast.success(roleSaved(row.name, field, on));
    } catch (err: any) {
      put(!on);
      toast.error(roleRefused(err));
    }
  };

  if (!isAdmin) return <Redirect to="/" />;

  return (
    <PageLayout kind="workspace" className="pt-4 space-y-5">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-primary">Locations</h1>
          <HelpDrawer title="About locations">
            <p>
              A location is a place stock can be. Every box you receive is filed to one,
              and the inventory page counts stock per location.
            </p>
            <p className="text-on-surface font-medium">The types</p>
            <p>
              <span className="text-on-surface">Warehouse:</span> a building you run.
              Switch on Inventory for the ones you receive into.
            </p>
            <p>
              <span className="text-on-surface">Vendor:</span> a supplier who is holding
              your material, usually a contract assembler you drop-shipped parts to.
              You do not create these here. Switch on "can receive" for the vendor and
              the location appears by itself, which keeps the two from drifting apart.
            </p>
            <p>
              <span className="text-on-surface">Event, Tour, Customer:</span> where gear
              goes after it leaves you. Not receiving destinations.
            </p>
            <p>
              <span className="text-on-surface">Unknown:</span> where something is when
              nobody is sure.
            </p>
            <p className="text-on-surface font-medium">Stock roles</p>
            <p>
              What a place is and what its stock is for are separate. Two switches on each
              row say what its stock is for, and a place can have both, one or neither.
            </p>
            <p>
              <span className="text-on-surface">Inventory:</span> shelf stock. Boxes are
              received here and picked from here.
            </p>
            <p>
              <span className="text-on-surface">Assembly:</span> the pool every run builds
              from. Picks land here and finished drones draw their parts from it. Only one
              place can be Assembly at a time.
            </p>
            <p className="text-on-surface font-medium">Retiring one</p>
            <p>
              Retiring takes a location out of every picker but keeps it on record.
              Nothing is deleted, because boxes you received there still point at it and
              the history should stay readable. You can put it back in service any time.
            </p>
            <p className="text-on-surface font-medium">If this list is empty</p>
            <p>
              A new deployment starts with no locations at all, and receiving will not
              work until there is at least one warehouse. Add your building and you are
              done.
            </p>
          </HelpDrawer>
        </div>
        <div className="flex items-center gap-3">
          <Appear show={retiredCount > 0} as="div">
            <Checkbox
              size="sm"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
              label={<span className="text-xs text-on-surface-variant">Show retired ({retiredCount})</span>}
            />
          </Appear>
          <button type="button" onClick={() => setCreating(true)} className={btnPrimary}>
            + New Location
          </button>
        </div>
      </div>

      <Reveal show={!loading && receivableCount === 0}>
        <Alert tone="warning" live={false} title="Receiving is blocked.">
          There is nowhere to receive stock into. Switch on Inventory for a warehouse,
          or switch on "can receive" for a vendor who holds your material.
        </Alert>
      </Reveal>

      {/* The vessel is the shared table's: shell, radius, scroll container and
          the face on every row. The retired filter stays up in the page header
          where it has always been — it decides what `visible` holds, and the
          table renders what it is given. */}
      <DataTable<LocationAdminRow>
        data={visible}
        columns={LOCATION_COLUMNS}
        getRowId={(r) => String(r.id)}
        meta={{
          onEdit: setEditing,
          setActive,
          setRole,
          pullSettings,
          setPullSetting,
          onAddArea: setAddingAreaTo,
          onGrow: setGrowing,
          onPrint: printLabels,
          expanded,
          onToggle: (a: LocationAdminRow) =>
            setExpanded((prev) => {
              const next = new Set(prev);
              if (next.has(a.id)) next.delete(a.id);
              else next.add(a.id);
              return next;
            }),
          gridOf,
          errors: rowErrors,
          canRestore: (r: LocationAdminRow) => {
            if (r.type === 'area') return false;
            if (r.type === 'section') return rows.find((p) => p.id === r.parent_location_id)?.is_active !== false;
            return true;
          },
        }}
        // A retired row reads as a record rather than a live place. Per row, so
        // it is a prop and not a column class.
        rowProps={(r) => ({ className: r.is_active ? '' : 'opacity-50' })}
        loading={loading}
        loadingContent={<LoadingBars />}
        empty={
          <EmptyState
            variant="area"
            title="No locations yet."
            action={
              <button type="button" onClick={() => setCreating(true)} className={btnPrimary}>
                Add your first location
              </button>
            }
          >
            Receiving needs somewhere to put things. Add the building you work
            out of and the receive screen will start working.
          </EmptyState>
        }
      />

      {addingAreaTo && (
        <AreaGridForm
          mode="add"
          parent={addingAreaTo}
          onClose={() => setAddingAreaTo(null)}
          onDone={() => {
            setAddingAreaTo(null);
            fetchRows();
          }}
        />
      )}

      {growing && (
        <AreaGridForm
          mode="grow"
          area={growing}
          columns={gridOf(growing).columns}
          rows={gridOf(growing).rows}
          existing={gridOf(growing).total}
          onClose={() => setGrowing(null)}
          onDone={() => {
            setGrowing(null);
            fetchRows();
          }}
        />
      )}

      {(creating || editing) && (
        <LocationForm
          row={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(saved) => {
            setRows((prev) => {
              const exists = prev.some((r) => r.id === saved.id);
              return exists ? prev.map((r) => (r.id === saved.id ? { ...r, ...saved } : r)) : [...prev, saved];
            });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </PageLayout>
  );
};

// ── create / edit ───────────────────────────────────────────────────────────

interface FormProps {
  row: LocationAdminRow | null;
  onClose: () => void;
  onSaved: (row: LocationAdminRow) => void;
}

const LocationForm: React.FC<FormProps> = ({ row, onClose, onSaved }) => {
  const api = useApi();
  const toast = useToast();
  const isEdit = !!row;
  // A vendor's location takes its name and type from the vendor record. Editing
  // them here would put the two out of step with nothing to reconcile them, so
  // the API refuses and the fields are disabled rather than silently failing.
  const vendorOwned = !!row?.vendor_id;

  const [name, setName] = useState(row?.name || '');
  const [type, setType] = useState<LocationAdminRow['type']>(row?.type || 'warehouse');
  const [address, setAddress] = useState(row?.address || '');
  const [saving, setSaving] = useState(false);

  const canSubmit = name.trim().length > 0 && !saving;
  const dirty =
    name.trim() !== (row?.name || '').trim() ||
    type !== (row?.type || 'warehouse') ||
    address.trim() !== (row?.address || '').trim();
  const typeHint = CREATABLE_TYPES.find((t) => t.value === type)?.hint;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = { address: address.trim() || null };
      if (!vendorOwned) {
        body.name = name.trim();
        body.type = type;
      }
      const saved = isEdit
        ? ((await api.patch(`/locations/${row!.id}`, { body })) as LocationAdminRow)
        : ((await api.post('/locations', { body })) as LocationAdminRow);
      toast.success(isEdit ? `${saved.name} saved` : `${saved.name} added`);
      onSaved(saved);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerShell title={isEdit ? 'Edit location' : 'New location'} onClose={onClose} dirty={dirty}>
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className={labelClass}>Name</span>
          <Field
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={vendorOwned}
            placeholder="e.g. 1900 Opdyke"
            autoFocus={!vendorOwned}
          />
          <Reveal show={vendorOwned}>
            <p className="text-xs text-on-surface-variant pt-1">
              This location belongs to a vendor. Rename it on the vendor record and it
              updates here.
            </p>
          </Reveal>
        </label>

        <div className="block">
          <span className={labelClass}>Type</span>
          <Select
            label="Type"
            value={type}
            onChange={(k) => setType(k as LocationAdminRow['type'])}
            disabled={vendorOwned}
            options={[
              ...(vendorOwned ? [{ key: 'vendor', label: 'Vendor' }] : []),
              ...(row?.type === 'repair' ? [{ key: 'repair', label: 'Repair' }] : []),
              ...CREATABLE_TYPES.map((t) => ({ key: t.value, label: t.label })),
            ]}
          />
          <Reveal show={!vendorOwned && !!typeHint}>
            <p className="text-xs text-on-surface-variant pt-1">{typeHint}</p>
          </Reveal>
        </div>

        <label className="block">
          <span className={labelClass}>Address</span>
          <Field
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Optional"
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button type="submit" disabled={!canSubmit} className={btnPrimary}>
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Add location'}
          </button>
        </div>
      </form>
    </DrawerShell>
  );
};

export default LocationsAdmin;
